import { useQuery } from "@tanstack/react-query";
import ApexCharts from "apexcharts";
import clsx from "clsx";
import { format, subDays } from "date-fns";
import { enqueueSnackbar } from "notistack";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Chart from "react-apexcharts";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";

import {
  SiteErrorTimeSerie,
  SiteVisitTimeSerie,
  SiteWafTimeSerie,
} from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import Button from "~/components/mantine/button/Button";
import Select from "~/components/mantine/select/Select";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import { AlertCard } from "~/components/ui/card/AlertCard";
import Spinner from "~/components/ui/spinner/Spinner";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useSiteContext } from "~/contexts/SiteContext";
import { useTeam } from "~/contexts/TeamProvider";
import { useTheme } from "~/contexts/ThemeContext";
import {
  spansMultipleYears,
  toEpoch,
  utcEndOfDay,
  utcStartOfDay,
} from "~/helpers/formatDate";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import BreakdownChart, {
  BreakdownChartVars,
  MAX_BREAKDOWN_ITEMS,
  formatChartValue,
} from "./BreakdownChart";
import ErrorsBreakdownTable from "./ErrorsBreakdownTable";
import Pill from "./Pill";
import RequestLog from "./RequestLog";
import Sparkline from "./Sparkline";
import styles from "./VisibilityEnginePage.module.scss";
import VisitorDetailsTable from "./VisitorDetailsTable";
import WafBreakdownTable from "./WafBreakdownTable";

// ── Constants ─────────────────────────────────────────────────

const TABS = ["traffic", "firewall", "errors", "logs"] as const;
type Tab = (typeof TABS)[number];

const TAB_CONFIG: Record<Tab, { label: string; accent: string }> = {
  traffic: { label: "Traffic", accent: styles.tabAccentPrimary },
  firewall: { label: "Firewall", accent: styles.tabAccentYellow },
  errors: { label: "Errors", accent: styles.tabAccentRed },
  logs: { label: "Logs", accent: styles.tabAccentGrey },
};

const DEFAULT_RANGE_DAYS = 7;

const RANGES = [
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
] as const;

type RangeDays = (typeof RANGES)[number]["days"];

// Quick filter presets for the Logs tab. The dropdown lives in the same
// header slot as the date-range pills (which are hidden on Logs since
// the request log is cursor-paginated). Each preset is just a search
// query string — picking one re-mounts RequestLog with that query as
// `initialSearch`, so the user lands on a focused subset right away.
// Order is curated, not alphabetical (most-actionable categories first).
const LOG_PRESETS: { label: string; query: string }[] = [
  { label: "Rate Limits", query: "guard:suspend" },
  { label: "WAF Detected", query: "waf:detect" },
  { label: "WAF Blocked", query: 'error:"WAF blocked"' },
  { label: "Access Errors", query: "status:401 or status:403" },
  { label: "Client Errors", query: "status:400..499" },
  { label: "Server Errors", query: "status:500.." },
  { label: "Cache Misses", query: "cache:miss" },
  { label: "Bots", query: "bot:true" },
];

// Cache window for time-series and breakdown queries. Within this window the
// same date range hits the React Query cache and skips the network entirely.
// Long enough to make tab switching feel instant; short enough that the data
// stays reasonably fresh.
const QUERY_STALE_TIME_MS = 5 * 60 * 1000;

type DayData = {
  date: string;
  /** Local-time start-of-day for this bucket (used to deep-link into Logs). */
  dayStart: Date;
  human: number;
  se: number;
  bots: number;
  llm: number;
};

// Pick the label format based on the selected range, not the data. This
// keeps the format stable when the data is sparse (e.g. a 90d range with
// only a couple of matching days) and makes gap-fill produce labels that
// match the rendered data labels.
function rangeDateFormat(rangeDays: number): string {
  const today = new Date();
  const earliest = subDays(today, rangeDays - 1);

  return spansMultipleYears([earliest, today]) ? "MMM dd, yyyy" : "MMM dd";
}

// Expand an accumulated map of daily buckets into a dense array covering
// every day in [today - (rangeDays-1), today]. Days without data get a
// zero-filled entry so charts render a continuous line instead of
// collapsing to a handful of points.
function fillDailyGaps<T extends { date: string; dayStart: Date }>(
  map: Map<string, T>,
  rangeDays: number,
  dateFormat: string,
  empty: Omit<T, "date" | "dayStart">,
): T[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const out: T[] = [];
  for (let offset = rangeDays - 1; offset >= 0; offset--) {
    const dayStart = new Date(today);
    dayStart.setDate(today.getDate() - offset);
    const date = format(dayStart, dateFormat);
    out.push((map.get(date) ?? { date, dayStart, ...empty }) as T);
  }

  return out;
}

function aggregateVisitsByDay(
  data: SiteVisitTimeSerie[],
  rangeDays: number,
): DayData[] {
  const dateFormat = rangeDateFormat(rangeDays);
  const map = new Map<string, DayData>();

  for (const item of data) {
    const itemDate = new Date(item.period * 1000);
    const date = format(itemDate, dateFormat);
    const existing = map.get(date) ?? {
      date,
      dayStart: new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate(),
      ),
      human: 0,
      se: 0,
      bots: 0,
      llm: 0,
    };

    if (item.bot && item.kind === "se") {
      existing.se += item.total;
    } else if (item.bot && item.kind === "llm") {
      existing.llm += item.total;
    } else if (item.bot && item.kind === "unknown") {
      existing.bots += item.total;
    } else {
      existing.human += item.total;
    }
    map.set(date, existing);
  }

  return fillDailyGaps(map, rangeDays, dateFormat, {
    human: 0,
    se: 0,
    bots: 0,
    llm: 0,
  });
}

type WafDayData = {
  date: string;
  dayStart: Date;
  log: number;
  allow: number;
  deny: number;
  detect: number;
};

function aggregateWafByDay(
  data: SiteWafTimeSerie[],
  rangeDays: number,
): WafDayData[] {
  const dateFormat = rangeDateFormat(rangeDays);
  const map = new Map<string, WafDayData>();

  for (const item of data) {
    const itemDate = new Date(item.period * 1000);
    const date = format(itemDate, dateFormat);
    const existing = map.get(date) ?? {
      date,
      dayStart: new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate(),
      ),
      log: 0,
      allow: 0,
      deny: 0,
      detect: 0,
    };

    existing[item.action] += item.total;
    map.set(date, existing);
  }

  return fillDailyGaps(map, rangeDays, dateFormat, {
    log: 0,
    allow: 0,
    deny: 0,
    detect: 0,
  });
}

type ErrorDayData = {
  date: string;
  dayStart: Date;
  connection: number;
  backend: number;
};

function aggregateErrorsByDay(
  data: SiteErrorTimeSerie[],
  rangeDays: number,
): ErrorDayData[] {
  const dateFormat = rangeDateFormat(rangeDays);
  const map = new Map<string, ErrorDayData>();

  for (const item of data) {
    const itemDate = new Date(item.period * 1000);
    const date = format(itemDate, dateFormat);
    const existing = map.get(date) ?? {
      date,
      dayStart: new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate(),
      ),
      connection: 0,
      backend: 0,
    };
    existing[item.kind] += item.total;
    map.set(date, existing);
  }

  return fillDailyGaps(map, rangeDays, dateFormat, {
    connection: 0,
    backend: 0,
  });
}

function aggregateTopN(
  data: SiteVisitTimeSerie[],
  groupBy: (item: SiteVisitTimeSerie) => string,
  filter?: (item: SiteVisitTimeSerie) => boolean,
  n = 5,
): { label: string; value: number }[] {
  const map = new Map<string, number>();
  const items = filter ? data.filter(filter) : data;

  for (const item of items) {
    const key = groupBy(item);
    if (!key) continue;
    map.set(key, (map.get(key) ?? 0) + item.total);
  }

  return Array.from(map.entries())
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, n);
}

// ── Theme-aware chart vars ─────────────────────────────────────

const BREAKDOWN_PALETTE_VARS = [
  "--functional-primary",
  "--service-green3",
  "--service-yellow3",
  "--service-red3",
  "--service-violete2",
] as const;

function readChartVars() {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();

  return {
    seriesColors: {
      se: v("--service-violete2"),
      human: v("--functional-primary"),
      bot: v("--text-grey"),
      llm: v("--service-yellow3"),
      wafLog: v("--service-yellow3"),
      wafAllow: v("--service-green3"),
      wafDeny: v("--service-red3"),
      wafDetect: v("--text-grey"),
      errConnection: v("--service-red3"),
      errBackend: v("--service-yellow3"),
    },
    tabAccents: {
      traffic: v("--functional-primary"),
      firewall: v("--service-yellow3"),
      errors: v("--service-red3"),
      logs: v("--text-grey"),
    },
    breakdownPalette: BREAKDOWN_PALETTE_VARS.map(v),
    textGrey: v("--text-grey"),
    gridBorder: v("--functional-grey2"),
  };
}

// ── Fake data for preview mode ─────────────────────────────────
//
// When a site hasn't enabled the AI Visibility Engine yet, we still render
// the page so users can see what they'd get. These generators produce
// deterministic but realistic-looking time series to drive the charts.

function mulberry32(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateFakeVisits(rangeDays: number): SiteVisitTimeSerie[] {
  const rand = mulberry32(42 + rangeDays);
  const now = Date.now();
  const dayMs = 86_400_000;
  const sources: {
    kind: "unknown" | "se" | "llm";
    bot: boolean;
    family: string;
    name: string;
    platform: string;
    base: number;
  }[] = [
    {
      kind: "unknown",
      bot: false,
      family: "Chrome",
      name: "Chrome",
      platform: "macOS",
      base: 820,
    },
    {
      kind: "unknown",
      bot: false,
      family: "Safari",
      name: "Safari",
      platform: "iOS",
      base: 460,
    },
    {
      kind: "unknown",
      bot: false,
      family: "Firefox",
      name: "Firefox",
      platform: "Windows",
      base: 200,
    },
    {
      kind: "se",
      bot: true,
      family: "Googlebot",
      name: "Googlebot",
      platform: "Other",
      base: 260,
    },
    {
      kind: "se",
      bot: true,
      family: "Bingbot",
      name: "Bingbot",
      platform: "Other",
      base: 110,
    },
    {
      kind: "llm",
      bot: true,
      family: "GPTBot",
      name: "GPTBot",
      platform: "Other",
      base: 95,
    },
    {
      kind: "llm",
      bot: true,
      family: "Claude-Web",
      name: "Claude-Web",
      platform: "Other",
      base: 70,
    },
    {
      kind: "unknown",
      bot: true,
      family: "Crawler",
      name: "Crawler",
      platform: "Other",
      base: 80,
    },
  ];

  const series: SiteVisitTimeSerie[] = [];

  for (let day = rangeDays - 1; day >= 0; day--) {
    const period = Math.floor((now - day * dayMs) / 1000);
    for (const s of sources) {
      const wave = 0.65 + Math.sin((rangeDays - day) / 3) * 0.25;
      const noise = 0.7 + rand() * 0.6;
      series.push({
        period,
        total: Math.max(0, Math.round(s.base * wave * noise)),
        bot: s.bot,
        kind: s.kind,
        family: s.family,
        name: s.name,
        platform: s.platform,
      });
    }
  }

  return series;
}

function generateFakeWaf(rangeDays: number): SiteWafTimeSerie[] {
  const rand = mulberry32(7 + rangeDays);
  const now = Date.now();
  const dayMs = 86_400_000;
  const actions: {
    action: "log" | "allow" | "deny" | "detect";
    base: number;
  }[] = [
    { action: "allow", base: 1500 },
    { action: "log", base: 60 },
    { action: "deny", base: 22 },
    { action: "detect", base: 9 },
  ];

  const series: SiteWafTimeSerie[] = [];

  for (let day = rangeDays - 1; day >= 0; day--) {
    const period = Math.floor((now - day * dayMs) / 1000);
    for (const a of actions) {
      const wave = 0.7 + Math.cos((rangeDays - day) / 4) * 0.22;
      const noise = 0.8 + rand() * 0.4;
      series.push({
        period,
        total: Math.max(0, Math.round(a.base * wave * noise)),
        rule_id: 942100,
        directive_id: 0,
        bot: false,
        action: a.action,
      });
    }
  }

  return series;
}

function generateFakeErrors(rangeDays: number): SiteErrorTimeSerie[] {
  const rand = mulberry32(13 + rangeDays);
  const now = Date.now();
  const dayMs = 86_400_000;
  const series: SiteErrorTimeSerie[] = [];

  for (let day = rangeDays - 1; day >= 0; day--) {
    const period = Math.floor((now - day * dayMs) / 1000);
    series.push(
      {
        period,
        total: Math.round(6 + rand() * 14),
        status: 502,
        bot: false,
        kind: "connection",
        category: "origin_timeout",
      },
      {
        period,
        total: Math.round(3 + rand() * 9),
        status: 500,
        bot: false,
        kind: "backend",
        category: "backend",
      },
    );
  }

  return series;
}

// ── Quick filters dropdown (Logs tab) ─────────────────────────

// Reuses the project's standard `Select` (the same one labelled "Crawl
// Frequency" in Site Settings) so the dropdown looks and behaves like
// every other field-style picker in the app. The component is treated
// as a one-shot trigger: we don't persist the picked value in the
// input — onChange forwards the preset's query string to the parent
// and the input clears immediately.
//
// The data items use the preset's array index as the option `value` so
// `onChange` can look up the original preset (label + query). The
// `renderOption` slot draws the two-column "label + mono query" layout
// inside each option, which keeps the search syntax visible and
// teaches users how the filters compose.
type LogPresetsDropdownProps = {
  onPick: (query: string) => void;
};

const LogPresetsDropdown = ({ onPick }: LogPresetsDropdownProps) => {
  // Bumped on every pick so the controlled `value` flips back to the
  // empty placeholder even when the user picks the same preset twice
  // in a row. Without this, the second pick would be a no-op (Mantine
  // skips onChange if value is unchanged).
  const [resetKey, setResetKey] = useState(0);

  return (
    <Select
      key={resetKey}
      placeholder="Quick filters"
      searchable={false}
      clearable={false}
      value={null}
      data={LOG_PRESETS.map((preset, idx) => ({
        value: String(idx),
        label: preset.label,
      }))}
      onChange={(val) => {
        if (val == null) return;
        const preset = LOG_PRESETS[Number(val)];
        if (!preset) return;
        onPick(preset.query);
        setResetKey((k) => k + 1);
      }}
      className={styles.logPresetsSelect}
      renderOption={({ option }) => {
        const preset = LOG_PRESETS[Number(option.value)];
        if (!preset) return null;

        return (
          <div className={styles.logPresetsOption}>
            <span className={styles.logPresetsOptionLabel}>{preset.label}</span>
            <span className={styles.logPresetsOptionQuery}>{preset.query}</span>
          </div>
        );
      }}
    />
  );
};

// ── Page ───────────────────────────────────────────────────────

const VisibilityEnginePage = () => {
  usePageTitle(PAGE_TITLES.trafficMetrics);

  const { id } = useParams();
  if (!id) throw new Error("Site ID is required");
  const siteId = parseInt(id);

  const navigate = useNavigate();
  const siteContext = useSiteContext();
  const { team } = useTeam();

  // The AI Visibility Engine is only available to teams whose subscription
  // includes the `prerendering` product flag — same condition SiteSettings
  // uses to expose the AI Visibility Engine settings tab. Accounts without
  // it (or sites whose application was reviewed and rejected) can't enable
  // the feature at all, so we send them back to the site root rather than
  // render a preview they can't act on.
  const aiVisibilityEngineAvailable =
    !!team?.subscription?.price?.product?.prerendering &&
    siteContext?.site?.site?.site_speed_status !== "rejected";

  useEffect(() => {
    if (!team) return;
    if (siteContext?.isLoadingSite) return;
    if (!aiVisibilityEngineAvailable) {
      navigate(routes.site.root({ id }), { replace: true });
    }
  }, [
    team,
    aiVisibilityEngineAvailable,
    siteContext?.isLoadingSite,
    navigate,
    id,
  ]);

  // For accounts that *do* have the feature available but haven't turned it
  // on for this site, we render the page in "preview mode": charts are
  // driven by deterministic fake data, the content below is blurred, and
  // an inline upsell card explains how to turn it on in Site Settings.
  const prerenderingEnabled = !!siteContext?.site?.site?.prerendering;
  const previewMode =
    aiVisibilityEngineAvailable &&
    !siteContext?.isLoadingSite &&
    !prerenderingEnabled;

  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>("traffic");
  const [logsConnected, setLogsConnected] = useState(false);
  const [rangeDays, setRangeDays] = useState<RangeDays>(DEFAULT_RANGE_DAYS);
  const [chartVars, setChartVars] = useState(readChartVars);
  // Pre-populated search for the Logs tab when deep-linked from a chart click.
  const [logsInitialSearch, setLogsInitialSearch] = useState("");
  // Bumped on each chart click so RequestLog re-mounts and picks up the
  // new initialSearch even if the user clicks the same day twice.
  const [logsSearchVersion, setLogsSearchVersion] = useState(0);

  useEffect(() => {
    setChartVars(readChartVars());
  }, [theme]);

  // Clear the chart-click search seed whenever the user navigates AWAY
  // from the Logs tab. Without this, the seed lingers in parent state
  // and re-applies the next time the user switches back to Logs (because
  // RequestLog reads `initialSearch` on mount and is conditionally
  // rendered, so it remounts on every tab switch).
  useEffect(() => {
    if (activeTab !== "logs") {
      setLogsInitialSearch("");
    }
  }, [activeTab]);

  // Switch to the Logs tab and pre-populate the search with a
  // from/to range for the given day. Triggered by chart point clicks.
  // `extraSearch` lets the caller add chart-specific filters — e.g. the
  // errors chart appends `status:500..599` so the user lands on the Logs
  // tab already filtered to server errors.
  const goToLogsForDay = useCallback((dayStart: Date, extraSearch?: string) => {
    const start = new Date(dayStart);
    const end = new Date(dayStart);
    end.setHours(23, 59, 59, 999);

    // Emit the day's local boundaries as explicit UTC instants. The Logs
    // search parses `from:`/`to:` in UTC (the frame log timestamps display
    // in), so a bare local-wall-clock string would be misread as UTC and
    // shift the window by the browser's offset. `Z` pins the exact instant.
    const isoUtc = (d: Date) => d.toISOString().replace(/\.\d+Z$/, "Z");

    const baseSearch = `from:${isoUtc(start)} to:${isoUtc(end)}`;
    setLogsInitialSearch(
      extraSearch ? `${baseSearch} ${extraSearch}` : baseSearch,
    );
    setLogsSearchVersion((v) => v + 1);
    setActiveTab("logs");
  }, []);

  const { from, to } = useMemo(() => {
    const now = new Date();

    return {
      from: toEpoch(utcStartOfDay(subDays(now, rangeDays - 1))),
      to: toEpoch(utcEndOfDay(now)),
    };
  }, [rangeDays]);

  const {
    data: visitsDataReal,
    isLoading: visitsLoadingReal,
    isError: visitsError,
  } = useQuery({
    queryKey: ["site-visits", siteId, from, to],
    enabled: !previewMode,
    staleTime: QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.visitsTimeSeriesDetail(siteId, { from, to }),
      );

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });
        throw result.error;
      }

      return result.data.data.site_visit_time_series;
    },
  });

  const { data: wafDataReal, isLoading: wafLoadingReal } = useQuery({
    queryKey: ["site-waf-events", siteId, from, to],
    enabled: !previewMode,
    staleTime: QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.wafEventsTimeSeriesDetail(siteId, { from, to }),
      );

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });
        throw result.error;
      }

      return result.data.data.site_waf_time_series;
    },
  });

  const { data: errorsDataReal, isLoading: errorsLoadingReal } = useQuery({
    queryKey: ["site-errors", siteId, from, to],
    enabled: !previewMode,
    staleTime: QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.errorsTimeSeriesDetail(siteId, { from, to }),
      );

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });
        throw result.error;
      }

      return result.data.data.site_error_time_series;
    },
  });

  // Swap in deterministic fake data when the Visibility Engine is not
  // enabled so the charts still render (blurred) behind the upsell card.
  const fakeVisits = useMemo(
    () => (previewMode ? generateFakeVisits(rangeDays) : null),
    [previewMode, rangeDays],
  );
  const fakeWaf = useMemo(
    () => (previewMode ? generateFakeWaf(rangeDays) : null),
    [previewMode, rangeDays],
  );
  const fakeErrors = useMemo(
    () => (previewMode ? generateFakeErrors(rangeDays) : null),
    [previewMode, rangeDays],
  );

  const visitsData = previewMode ? fakeVisits : visitsDataReal;
  const wafData = previewMode ? fakeWaf : wafDataReal;
  const errorsData = previewMode ? fakeErrors : errorsDataReal;
  const visitsLoading = previewMode ? false : visitsLoadingReal;
  const wafLoading = previewMode ? false : wafLoadingReal;
  const errorsLoading = previewMode ? false : errorsLoadingReal;

  // Breakdown queries are only fetched for the active tab — they're heavier
  // than the time series and only consumed by the per-tab tables.
  const { data: visitorsData, isLoading: visitorsLoading } = useQuery({
    queryKey: ["site-visitors-breakdown", siteId, from, to],
    enabled: !previewMode && activeTab === "traffic",
    staleTime: QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.visitorsBreakdownDetail(siteId, { from, to }),
      );

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });
        throw result.error;
      }

      return result.data.data.site_visitor_breakdown;
    },
  });

  const { data: wafBreakdownData, isLoading: wafBreakdownLoading } = useQuery({
    queryKey: ["site-waf-breakdown", siteId, from, to],
    enabled: !previewMode && activeTab === "firewall",
    staleTime: QUERY_STALE_TIME_MS,
    queryFn: async () => {
      const result = await tryCatch(
        apiClient.sites.wafEventsBreakdownDetail(siteId, { from, to }),
      );

      if (result.error) {
        enqueueSnackbar(messageFromError(result.error), {
          variant: "error",
          persist: true,
        });
        throw result.error;
      }

      return result.data.data.site_waf_breakdown;
    },
  });

  const { data: errorsBreakdownData, isLoading: errorsBreakdownLoading } =
    useQuery({
      queryKey: ["site-errors-breakdown", siteId, from, to],
      enabled: !previewMode && activeTab === "errors",
      staleTime: QUERY_STALE_TIME_MS,
      queryFn: async () => {
        const result = await tryCatch(
          apiClient.sites.errorsBreakdownDetail(siteId, { from, to }),
        );

        if (result.error) {
          enqueueSnackbar(messageFromError(result.error), {
            variant: "error",
            persist: true,
          });
          throw result.error;
        }

        return result.data.data.site_error_breakdown;
      },
    });

  const isLoading = visitsLoading || wafLoading;

  // ── Derived data ─────────────────────────────────────────────

  const aggregated = useMemo(
    () => (visitsData ? aggregateVisitsByDay(visitsData, rangeDays) : []),
    [visitsData, rangeDays],
  );

  const wafAggregated = useMemo(
    () => (wafData ? aggregateWafByDay(wafData, rangeDays) : []),
    [wafData, rangeDays],
  );

  const errorsAggregated = useMemo(
    () => (errorsData ? aggregateErrorsByDay(errorsData, rangeDays) : []),
    [errorsData, rangeDays],
  );

  const categories = aggregated.map((d) => d.date);
  const wafCategories = wafAggregated.map((d) => d.date);

  const totalSE = aggregated.reduce((s, d) => s + d.se, 0);
  const totalHuman = aggregated.reduce((s, d) => s + d.human, 0);
  const totalBots = aggregated.reduce((s, d) => s + d.bots, 0);
  const totalLLM = aggregated.reduce((s, d) => s + d.llm, 0);
  const totalWafLog = wafAggregated.reduce((s, d) => s + d.log, 0);
  const totalWafAllow = wafAggregated.reduce((s, d) => s + d.allow, 0);
  const totalWafDeny = wafAggregated.reduce((s, d) => s + d.deny, 0);
  const totalWafDetect = wafAggregated.reduce((s, d) => s + d.detect, 0);
  const totalErrors = errorsAggregated.reduce(
    (s, d) => s + d.connection + d.backend,
    0,
  );

  // ── Sparkline data (total per day for each tab) ──────────────
  const trafficSparkline = aggregated.map(
    (d) => d.human + d.bots + d.se + d.llm,
  );
  const firewallSparkline = wafAggregated.map(
    (d) => d.log + d.allow + d.deny + d.detect,
  );
  const errorsSparkline = errorsAggregated.map((d) => d.connection + d.backend);

  const platforms = useMemo(
    () =>
      aggregateTopN(
        visitsData ?? [],
        (item) => item.platform,
        (item) => !item.bot,
        MAX_BREAKDOWN_ITEMS,
      ),
    [visitsData],
  );

  const browsers = useMemo(
    () =>
      aggregateTopN(
        visitsData ?? [],
        (item) => item.family,
        (item) => !item.bot,
        MAX_BREAKDOWN_ITEMS,
      ),
    [visitsData],
  );

  const families = useMemo(
    () =>
      aggregateTopN(
        visitsData ?? [],
        (item) => item.family,
        undefined,
        MAX_BREAKDOWN_ITEMS,
      ),
    [visitsData],
  );

  const searchEngines = useMemo(
    () =>
      aggregateTopN(
        visitsData ?? [],
        (item) => item.name,
        (item) => item.bot && item.kind === "se",
        MAX_BREAKDOWN_ITEMS,
      ),
    [visitsData],
  );

  // ── Chart series ─────────────────────────────────────────────

  const mainChartSeries = [
    {
      name: "Human",
      type: "area" as const,
      data: aggregated.map((d) => d.human),
    },
    {
      name: "Bots",
      type: "area" as const,
      data: aggregated.map((d) => d.bots),
    },
    {
      name: "Search Engines",
      type: "area" as const,
      data: aggregated.map((d) => d.se),
    },
    {
      name: "LLM",
      type: "area" as const,
      data: aggregated.map((d) => d.llm),
    },
  ];

  const {
    se,
    human,
    bot,
    llm,
    wafLog,
    wafAllow,
    wafDeny,
    wafDetect,
    errConnection,
    errBackend,
  } = chartVars.seriesColors;

  const firewallSeriesAll = [
    {
      name: "Logged",
      color: wafLog,
      type: "area" as const,
      data: wafAggregated.map((d) => d.log),
      total: totalWafLog,
    },
    {
      name: "Allowed",
      color: wafAllow,
      type: "area" as const,
      data: wafAggregated.map((d) => d.allow),
      total: totalWafAllow,
    },
    {
      name: "Denied",
      color: wafDeny,
      type: "area" as const,
      data: wafAggregated.map((d) => d.deny),
      total: totalWafDeny,
    },
    {
      name: "Detected",
      color: wafDetect,
      type: "area" as const,
      data: wafAggregated.map((d) => d.detect),
      total: totalWafDetect,
    },
  ];

  const firewallSeries = firewallSeriesAll.map(({ name, type, data }) => ({
    name,
    type,
    data,
  }));

  const firewallColors = firewallSeriesAll.map((s) => s.color);

  // ── Errors chart series ──────────────────────────────────────

  const errorsCategories = errorsAggregated.map((d) => d.date);
  const totalErrConnection = errorsAggregated.reduce(
    (s, d) => s + d.connection,
    0,
  );
  const totalErrBackend = errorsAggregated.reduce((s, d) => s + d.backend, 0);

  const errorsSeriesAll = [
    {
      name: "Connection",
      color: errConnection,
      type: "area" as const,
      data: errorsAggregated.map((d) => d.connection),
      total: totalErrConnection,
    },
    {
      name: "Backend",
      color: errBackend,
      type: "area" as const,
      data: errorsAggregated.map((d) => d.backend),
      total: totalErrBackend,
    },
  ];

  const errorsSeries = errorsSeriesAll.map(({ name, type, data }) => ({
    name,
    type,
    data,
  }));

  const errorsColors = errorsSeriesAll.map((s) => s.color);

  const [hiddenMainSeries, setHiddenMainSeries] = useState<Set<string>>(
    new Set(),
  );
  const [hiddenFirewallSeries, setHiddenFirewallSeries] = useState<Set<string>>(
    new Set(),
  );
  const [hiddenErrorsSeries, setHiddenErrorsSeries] = useState<Set<string>>(
    new Set(),
  );

  const toggleSeries = (
    chartId: string,
    totalSeriesCount: number,
    setHidden: React.Dispatch<React.SetStateAction<Set<string>>>,
    name: string,
  ) => {
    setHidden((prev) => {
      const next = new Set(prev);

      if (next.has(name)) {
        next.delete(name);
        ApexCharts.exec(chartId, "showSeries", name);

        return next;
      }

      // Don't hide the last visible series
      if (prev.size >= totalSeriesCount - 1) return prev;

      next.add(name);
      ApexCharts.exec(chartId, "hideSeries", name);

      return next;
    });
  };

  const fakeBreadcrumbs = [
    { title: PAGE_TITLES.dashboard, link: routes.home() },
    {
      title: PAGE_TITLES.trafficMetrics,
      link: routes.site.trafficMetrics({ id }),
    },
  ];

  const totalTraffic = totalHuman + totalBots + totalSE + totalLLM;
  const totalWaf = totalWafLog + totalWafAllow + totalWafDeny + totalWafDetect;

  const tabData: Record<
    Tab,
    { total: number; sparkline: number[]; loading: boolean }
  > = {
    traffic: {
      total: totalTraffic,
      sparkline: trafficSparkline,
      loading: visitsLoading,
    },
    firewall: {
      total: totalWaf,
      sparkline: firewallSparkline,
      loading: wafLoading,
    },
    errors: {
      total: totalErrors,
      sparkline: errorsSparkline,
      loading: errorsLoading,
    },
    logs: { total: 0, sparkline: [], loading: false },
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{PAGE_TITLES.trafficMetrics}</h1>
        <Breadcrumbs fakeBreadcrumbs={fakeBreadcrumbs} />
        {!previewMode &&
          (activeTab === "logs" ? (
            <LogPresetsDropdown
              onPick={(query) => {
                setLogsInitialSearch(query);
                setLogsSearchVersion((v) => v + 1);
              }}
            />
          ) : (
            <div className={styles.rangeSelector}>
              {RANGES.map((r) => (
                <Pill
                  key={r.days}
                  active={rangeDays === r.days}
                  onClick={() => setRangeDays(r.days)}
                  multi={false}
                >
                  {r.label}
                </Pill>
              ))}
            </div>
          ))}
      </div>

      {previewMode && (
        <AlertCard
          type="neutral"
          className={styles.previewAlert}
          title="AI Visibility Engine is not enabled"
          content={
            <>
              The charts and tables below show sample data. Enable{" "}
              <strong>AI Visibility Engine Optimization</strong> in your Site
              Settings to start seeing real request logs, WAF events, and error
              analytics for your site — usually within minutes of turning it on.
            </>
          }
          actions={
            <Button
              onClick={() =>
                navigate(routes.site.visibilityEngineSettings({ id }))
              }
            >
              Enable AI Visibility Engine
            </Button>
          }
        />
      )}

      <div
        className={clsx(previewMode && styles.previewContent)}
        aria-hidden={previewMode || undefined}
      >
        {/* ── Tab bar ─────────────────────────────────────────── */}
        <div className={styles.tabBar}>
          {TABS.map((tab) => {
            const { total, sparkline, loading } = tabData[tab];
            const isActive = activeTab === tab;
            const isLogs = tab === "logs";

            return (
              <button
                key={tab}
                className={clsx(
                  styles.tab,
                  TAB_CONFIG[tab].accent,
                  isActive && styles.tabActive,
                )}
                onClick={() => setActiveTab(tab)}
              >
                <span
                  className={clsx(
                    styles.tabLabel,
                    !isActive && styles.tabLabelInactive,
                  )}
                >
                  {TAB_CONFIG[tab].label}
                </span>
                <div className={styles.tabBody}>
                  {isLogs ? (
                    <span className={styles.tabMetricLive}>
                      <span
                        className={
                          logsConnected
                            ? styles.tabMetricLiveDot
                            : styles.tabMetricLiveDotOff
                        }
                      />
                      Live
                    </span>
                  ) : (
                    <>
                      <span
                        className={clsx(
                          styles.tabMetric,
                          !isActive && styles.tabMetricInactive,
                        )}
                      >
                        {loading ? (
                          <Skeleton width={60} height={28} />
                        ) : (
                          total.toLocaleString()
                        )}
                      </span>
                      {!loading && sparkline.length >= 2 && (
                        <Sparkline
                          data={sparkline}
                          color={chartVars.tabAccents[tab]}
                        />
                      )}
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* ── Traffic tab ─────────────────────────────────────── */}
        {activeTab === "traffic" && (
          <>
            <div className={styles.chartCard}>
              <div className={styles.chartCardHeader}>
                <span className={styles.chartCardTitle}>Visits over time</span>
              </div>
              <div className={styles.statsRow}>
                <StatCard
                  label="Human"
                  value={totalHuman}
                  isLoading={isLoading}
                  accent="primary"
                  dimmed={
                    (!isLoading && totalHuman === 0) ||
                    hiddenMainSeries.has("Human")
                  }
                  onClick={
                    !isLoading && totalHuman > 0
                      ? () =>
                          toggleSeries(
                            "visits-chart",
                            4,
                            setHiddenMainSeries,
                            "Human",
                          )
                      : undefined
                  }
                />
                <StatCard
                  label="Bots"
                  value={totalBots}
                  isLoading={isLoading}
                  accent="grey"
                  dimmed={
                    (!isLoading && totalBots === 0) ||
                    hiddenMainSeries.has("Bots")
                  }
                  onClick={
                    !isLoading && totalBots > 0
                      ? () =>
                          toggleSeries(
                            "visits-chart",
                            4,
                            setHiddenMainSeries,
                            "Bots",
                          )
                      : undefined
                  }
                />
                <StatCard
                  label="Search Engines"
                  value={totalSE}
                  isLoading={isLoading}
                  accent="purple"
                  dimmed={
                    (!isLoading && totalSE === 0) ||
                    hiddenMainSeries.has("Search Engines")
                  }
                  onClick={
                    !isLoading && totalSE > 0
                      ? () =>
                          toggleSeries(
                            "visits-chart",
                            4,
                            setHiddenMainSeries,
                            "Search Engines",
                          )
                      : undefined
                  }
                />
                <StatCard
                  label="LLM"
                  value={totalLLM}
                  isLoading={isLoading}
                  accent="yellow"
                  dimmed={
                    (!isLoading && totalLLM === 0) ||
                    hiddenMainSeries.has("LLM")
                  }
                  onClick={
                    !isLoading && totalLLM > 0
                      ? () =>
                          toggleSeries(
                            "visits-chart",
                            4,
                            setHiddenMainSeries,
                            "LLM",
                          )
                      : undefined
                  }
                />
              </div>

              <div className={styles.chartWrapper}>
                {isLoading && (
                  <div className={styles.chartLoadingOverlay}>
                    <Spinner size="32px" />
                  </div>
                )}
                {visitsError && !isLoading && (
                  <div className={styles.chartEmptyOverlay}>
                    Failed to load visits data.
                  </div>
                )}
                {!isLoading && !visitsError && aggregated.length === 0 && (
                  <div className={styles.chartEmptyOverlay}>
                    No visit data available for this period.
                  </div>
                )}
                {!visitsError && aggregated.length > 0 && (
                  <Chart
                    options={{
                      chart: {
                        id: "visits-chart",
                        type: "line",
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        fontFamily: "Lato, sans-serif",
                        events: {
                          markerClick: (_e, _ctx, { dataPointIndex }) => {
                            const day = aggregated[dataPointIndex];
                            if (day) goToLogsForDay(day.dayStart);
                          },
                        },
                      },
                      colors: [human, bot, se, llm],
                      fill: {
                        type: "gradient",
                        gradient: {
                          shadeIntensity: 1,
                          opacityFrom: 0.35,
                          opacityTo: 0.02,
                          stops: [0, 100],
                        },
                      },
                      stroke: { curve: "smooth", width: 2 },
                      dataLabels: { enabled: false },
                      grid: {
                        borderColor: chartVars.gridBorder,
                        strokeDashArray: 4,
                      },
                      xaxis: {
                        categories,
                        labels: {
                          style: {
                            colors: chartVars.textGrey,
                            fontSize: "12px",
                            fontFamily: "Lato, sans-serif",
                          },
                          rotate: -30,
                          hideOverlappingLabels: true,
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tickAmount: Math.min(categories.length, 14),
                      },
                      yaxis: {
                        labels: {
                          style: {
                            colors: chartVars.textGrey,
                            fontSize: "12px",
                            fontFamily: "Lato, sans-serif",
                          },
                          formatter: formatChartValue,
                        },
                      },
                      tooltip: {
                        theme,
                        y: { formatter: formatChartValue },
                      },
                      legend: { show: false },
                    }}
                    series={mainChartSeries}
                    type="line"
                    height={240}
                  />
                )}
              </div>
              <div className={styles.chartCardFootnote}>
                Tip: click any point on the chart to see the matching logs.
              </div>
            </div>

            <BreakdownRow
              isLoading={visitsLoading}
              chartVars={chartVars}
              theme={theme}
              charts={[
                { title: "Top Initiators", data: families },
                { title: "Top Platforms", data: platforms },
                { title: "Top Browsers", data: browsers },
                {
                  title: "Search Engines",
                  data: searchEngines,
                  emptyText: "No search engine traffic for this period.",
                },
              ]}
            />

            <VisitorDetailsTable
              data={visitorsData ?? []}
              isLoading={visitorsLoading}
            />
          </>
        )}

        {/* ── Firewall tab ────────────────────────────────────── */}
        {activeTab === "firewall" && (
          <>
            <div className={styles.chartCard}>
              <div className={styles.chartCardHeader}>
                <span className={styles.chartCardTitle}>Firewall</span>
              </div>
              <div className={styles.statsRow}>
                <StatCard
                  label="Logged"
                  value={totalWafLog}
                  isLoading={wafLoading}
                  accent="yellow"
                  dimmed={
                    (!wafLoading && totalWafLog === 0) ||
                    hiddenFirewallSeries.has("Logged")
                  }
                  onClick={
                    !wafLoading && totalWafLog > 0
                      ? () =>
                          toggleSeries(
                            "firewall-chart",
                            firewallSeries.length,
                            setHiddenFirewallSeries,
                            "Logged",
                          )
                      : undefined
                  }
                />
                <StatCard
                  label="Allowed"
                  value={totalWafAllow}
                  isLoading={wafLoading}
                  accent="green"
                  dimmed={
                    (!wafLoading && totalWafAllow === 0) ||
                    hiddenFirewallSeries.has("Allowed")
                  }
                  onClick={
                    !wafLoading && totalWafAllow > 0
                      ? () =>
                          toggleSeries(
                            "firewall-chart",
                            firewallSeries.length,
                            setHiddenFirewallSeries,
                            "Allowed",
                          )
                      : undefined
                  }
                />
                <StatCard
                  label="Denied"
                  value={totalWafDeny}
                  isLoading={wafLoading}
                  accent="red"
                  dimmed={
                    (!wafLoading && totalWafDeny === 0) ||
                    hiddenFirewallSeries.has("Denied")
                  }
                  onClick={
                    !wafLoading && totalWafDeny > 0
                      ? () =>
                          toggleSeries(
                            "firewall-chart",
                            firewallSeries.length,
                            setHiddenFirewallSeries,
                            "Denied",
                          )
                      : undefined
                  }
                />
                <StatCard
                  label="Detected"
                  value={totalWafDetect}
                  isLoading={wafLoading}
                  accent="grey"
                  dimmed={
                    (!wafLoading && totalWafDetect === 0) ||
                    hiddenFirewallSeries.has("Detected")
                  }
                  onClick={
                    !wafLoading && totalWafDetect > 0
                      ? () =>
                          toggleSeries(
                            "firewall-chart",
                            firewallSeries.length,
                            setHiddenFirewallSeries,
                            "Detected",
                          )
                      : undefined
                  }
                />
              </div>

              <div className={styles.chartWrapper}>
                {wafLoading && (
                  <div className={styles.chartLoadingOverlay}>
                    <Spinner size="32px" />
                  </div>
                )}
                {!wafLoading && wafAggregated.length === 0 && (
                  <div className={styles.chartEmptyOverlay}>
                    No threats for this period.
                  </div>
                )}
                {wafAggregated.length > 0 && (
                  <Chart
                    options={{
                      chart: {
                        id: "firewall-chart",
                        type: "line",
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        fontFamily: "Lato, sans-serif",
                        events: {
                          markerClick: (_e, _ctx, { dataPointIndex }) => {
                            const day = wafAggregated[dataPointIndex];
                            if (day)
                              goToLogsForDay(
                                day.dayStart,
                                "(rule:0.. or directive:0..)",
                              );
                          },
                        },
                      },
                      colors: firewallColors,
                      fill: {
                        type: "gradient",
                        gradient: {
                          shadeIntensity: 1,
                          opacityFrom: 0.35,
                          opacityTo: 0.02,
                          stops: [0, 100],
                        },
                      },
                      stroke: { curve: "smooth", width: 2 },
                      dataLabels: { enabled: false },
                      grid: {
                        borderColor: chartVars.gridBorder,
                        strokeDashArray: 4,
                      },
                      xaxis: {
                        categories: wafCategories,
                        labels: {
                          style: {
                            colors: chartVars.textGrey,
                            fontSize: "12px",
                            fontFamily: "Lato, sans-serif",
                          },
                          rotate: -30,
                          hideOverlappingLabels: true,
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tickAmount: Math.min(wafCategories.length, 14),
                      },
                      yaxis: {
                        labels: {
                          style: {
                            colors: chartVars.textGrey,
                            fontSize: "12px",
                            fontFamily: "Lato, sans-serif",
                          },
                          formatter: formatChartValue,
                        },
                      },
                      tooltip: {
                        theme,
                        y: { formatter: formatChartValue },
                      },
                      legend: { show: false },
                    }}
                    series={firewallSeries}
                    type="line"
                    height={240}
                  />
                )}
              </div>
              <div className={styles.chartCardFootnote}>
                Tip: click any point on the chart to see the matching logs.
              </div>
            </div>
            <WafBreakdownTable
              data={wafBreakdownData ?? []}
              isLoading={wafBreakdownLoading}
            />
          </>
        )}

        {/* ── Errors tab ────────────────────────────────────── */}
        {activeTab === "errors" && (
          <>
            <div className={styles.chartCard}>
              <div className={styles.chartCardHeader}>
                <span className={styles.chartCardTitle}>Errors over time</span>
              </div>
              <div className={styles.statsRow}>
                <StatCard
                  label="Connection"
                  value={totalErrConnection}
                  isLoading={errorsLoading}
                  accent="red"
                  dimmed={
                    (!errorsLoading && totalErrConnection === 0) ||
                    hiddenErrorsSeries.has("Connection")
                  }
                  onClick={
                    !errorsLoading && totalErrConnection > 0
                      ? () =>
                          toggleSeries(
                            "errors-chart",
                            errorsSeries.length,
                            setHiddenErrorsSeries,
                            "Connection",
                          )
                      : undefined
                  }
                />
                <StatCard
                  label="Backend"
                  value={totalErrBackend}
                  isLoading={errorsLoading}
                  accent="yellow"
                  dimmed={
                    (!errorsLoading && totalErrBackend === 0) ||
                    hiddenErrorsSeries.has("Backend")
                  }
                  onClick={
                    !errorsLoading && totalErrBackend > 0
                      ? () =>
                          toggleSeries(
                            "errors-chart",
                            errorsSeries.length,
                            setHiddenErrorsSeries,
                            "Backend",
                          )
                      : undefined
                  }
                />
              </div>

              <div className={styles.chartWrapper}>
                {errorsLoading && (
                  <div className={styles.chartLoadingOverlay}>
                    <Spinner size="32px" />
                  </div>
                )}
                {!errorsLoading && errorsAggregated.length === 0 && (
                  <div className={styles.chartEmptyOverlay}>
                    No errors for this period.
                  </div>
                )}
                {errorsAggregated.length > 0 && (
                  <Chart
                    options={{
                      chart: {
                        id: "errors-chart",
                        type: "line",
                        toolbar: { show: false },
                        zoom: { enabled: false },
                        fontFamily: "Lato, sans-serif",
                        events: {
                          markerClick: (_e, _ctx, { dataPointIndex }) => {
                            const day = errorsAggregated[dataPointIndex];
                            if (day)
                              goToLogsForDay(day.dayStart, "status:500..599");
                          },
                        },
                      },
                      colors: errorsColors,
                      fill: {
                        type: "gradient",
                        gradient: {
                          shadeIntensity: 1,
                          opacityFrom: 0.35,
                          opacityTo: 0.02,
                          stops: [0, 100],
                        },
                      },
                      stroke: { curve: "smooth", width: 2 },
                      dataLabels: { enabled: false },
                      grid: {
                        borderColor: chartVars.gridBorder,
                        strokeDashArray: 4,
                      },
                      xaxis: {
                        categories: errorsCategories,
                        labels: {
                          style: {
                            colors: chartVars.textGrey,
                            fontSize: "12px",
                            fontFamily: "Lato, sans-serif",
                          },
                          rotate: -30,
                          hideOverlappingLabels: true,
                        },
                        axisBorder: { show: false },
                        axisTicks: { show: false },
                        tickAmount: Math.min(errorsCategories.length, 14),
                      },
                      yaxis: {
                        labels: {
                          style: {
                            colors: chartVars.textGrey,
                            fontSize: "12px",
                            fontFamily: "Lato, sans-serif",
                          },
                          formatter: formatChartValue,
                        },
                      },
                      tooltip: {
                        theme,
                        y: { formatter: formatChartValue },
                      },
                      legend: { show: false },
                    }}
                    series={errorsSeries}
                    type="line"
                    height={240}
                  />
                )}
              </div>
              <div className={styles.chartCardFootnote}>
                Tip: click any point on the chart to see the matching logs.
              </div>
            </div>
            <ErrorsBreakdownTable
              data={errorsBreakdownData ?? []}
              isLoading={errorsBreakdownLoading}
            />
          </>
        )}

        {/* ── Logs tab ──────────────────────────────────────── */}
        {activeTab === "logs" &&
          (previewMode ? (
            <div className={styles.logsPreview}>
              {Array.from({ length: 14 }, (_, i) => (
                <Skeleton key={i} height={22} />
              ))}
            </div>
          ) : (
            <RequestLog
              // Re-mount when a new chart-driven search is pushed so the new
              // initialSearch is picked up (initialSearch only seeds state on mount).
              key={logsSearchVersion}
              siteId={siteId}
              onConnectionChange={setLogsConnected}
              initialSearch={logsInitialSearch}
            />
          ))}
      </div>
    </div>
  );
};

// ── BreakdownRow ───────────────────────────────────────────────

type BreakdownRowChart = {
  title: string;
  data: { label: string; value: number }[];
  emptyText?: string;
};

type BreakdownRowProps = {
  charts: BreakdownRowChart[];
  isLoading: boolean;
  chartVars: BreakdownChartVars;
  theme: "light" | "dark";
};

const MIN_BREAKDOWN_CHART_WIDTH = 400;

function computeBreakdownCols(n: number, containerWidth: number): string {
  if (n <= 1) return "1fr";

  const maxByWidth =
    containerWidth > 0
      ? Math.floor(containerWidth / MIN_BREAKDOWN_CHART_WIDTH)
      : n;

  let cols = Math.min(n, Math.max(1, maxByWidth));

  // Balanced rows: n=4 should be 2×2, not 3+1
  if (n === 4 && cols >= 3) cols = 2;

  return `repeat(${cols}, 1fr)`;
}

const BreakdownRow = ({
  charts,
  isLoading,
  chartVars,
  theme,
}: BreakdownRowProps) => {
  const visible = isLoading ? charts : charts.filter((c) => c.data.length > 0);
  const rowRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  if (!isLoading && visible.length === 0) return null;

  return (
    <div
      ref={rowRef}
      className={styles.breakdownRow}
      style={{
        gridTemplateColumns: computeBreakdownCols(
          visible.length,
          containerWidth,
        ),
      }}
    >
      {visible.map((chart) => (
        <BreakdownChart
          key={chart.title}
          title={chart.title}
          data={chart.data}
          isLoading={isLoading}
          chartVars={chartVars}
          theme={theme}
          emptyText={chart.emptyText}
        />
      ))}
    </div>
  );
};

// ── StatCard ───────────────────────────────────────────────────

type StatCardProps = {
  label: string;
  value: number;
  isLoading: boolean;
  accent: "primary" | "green" | "grey" | "red" | "purple" | "yellow";
  onClick?: () => void;
  dimmed?: boolean;
};

const accentClass = {
  primary: styles.statCardPrimary,
  green: styles.statCardGreen,
  grey: styles.statCardGrey,
  red: styles.statCardRed,
  purple: styles.statCardPurple,
  yellow: styles.statCardYellow,
} as const;

const StatCard = ({
  label,
  value,
  isLoading,
  accent,
  onClick,
  dimmed,
}: StatCardProps) => (
  <div
    className={clsx(styles.statCard, accentClass[accent], {
      [styles.statCardClickable]: !!onClick,
      [styles.statCardDimmed]: dimmed,
    })}
    onClick={onClick}
    role={onClick ? "button" : undefined}
  >
    <span className={styles.statLabel}>{label}</span>
    {isLoading ? (
      <Skeleton
        width={80}
        height={24}
        containerClassName={styles.skeletonContainer}
      />
    ) : (
      <span className={styles.statValue}>{value.toLocaleString()}</span>
    )}
  </div>
);

export default VisibilityEnginePage;
